import { ChangeEvent } from 'react';

import {
    ChevronDownIcon,
    Controller,
    RadioButtonGroup,
    TextField,
} from '@selina-finance/ui';

import { DebtLabelEnum } from '@src/app/formModels/eligibility/debtCon';
import { months, nextFiveYears } from '@src/shared/helpers/date';
import { radioButtonOptions } from '@src/shared/helpers/input';
import DebtConCallOut from '@src/ui/components/CallOut/DebtConCallOut';
import SelectBox from '@src/ui/components/SelectBox/SelectBox';

interface CreditCardMailOrderFormProps {
    errors: any;
    values: any;
    touched: any;
    setTouched: (fields: { [field: string]: boolean }) => void;
    setFieldValue: (field: string, value: any) => void;
    handleChange: (e: ChangeEvent<any>) => void;
    handleBlur: (e: ChangeEvent<any>) => void;
    getError: (field: string) => string | undefined;
}

const CreditCardMailOrderForm = ({
    errors,
    values,
    touched,
    setTouched,
    setFieldValue,
    handleChange,
    handleBlur,
    getError,
}: CreditCardMailOrderFormProps) => {
    if (!values.endMonth && !values.endYear) {
        values.endMonth = '';
        values.endYear = '';
    }

    if (values.interestFreeEndDate) {
        values.endMonth = values.interestFreeEndDate.split('/')[1];
        values.endYear = values.interestFreeEndDate.split('/')[2];
    }

    const handleMonthChange = (e: ChangeEvent<HTMLInputElement>) => {
        const interestFreeEndDate = `01/${e.currentTarget.value}/${values.endYear}`;
        setFieldValue('interestFreeEndDate', interestFreeEndDate);
        setFieldValue('endMonth', e.currentTarget.value);
    };

    const handleYearChange = (e: ChangeEvent<HTMLInputElement>) => {
        const interestFreeEndDate = `01/${values.endMonth}/${e.currentTarget.value}`;
        setFieldValue('interestFreeEndDate', interestFreeEndDate);
        setFieldValue('endYear', e.currentTarget.value);
    };

    const onBlur = () => {
        setTouched({ ...touched, endYear: true });
        setTouched({ ...touched, interestFreeEndDate: true });
    };

    const interestLabel =
        values.interestFreePeriod === 'true'
            ? 'APR after promotional period ends?'
            : "What's the interest rate?";

    return (
        <>
            <Controller
                label='Are you in a 0% APR promotional period?'
                labelSize='small'
            >
                <RadioButtonGroup
                    name='interestFreePeriod'
                    value={values.interestFreePeriod?.toString()}
                    error={{
                        hasError: false,
                        errorMessage: getError('interestFreePeriod'),
                    }}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        handleChange(e);
                        handleBlur(e);
                    }}
                    options={radioButtonOptions}
                />
            </Controller>

            {(values.interestFreePeriod === 'true' ||
                values.interestFreePeriod === true) && (
                <>
                    <Controller
                        label='When does your 0% period end?'
                        labelSize='small'
                    >
                        <div className='grid gap-4 items-start grid-cols-2'>
                            <SelectBox
                                items={months}
                                suffix={<ChevronDownIcon />}
                                label='Month'
                                labelSize='small'
                                name='endMonth'
                                placeholder='Select'
                                validState={!!values.endMonth}
                                value={values.endMonth || ''}
                                onChange={handleMonthChange}
                                error={getError('endMonth')}
                                onBlur={handleBlur}
                            />
                            <SelectBox
                                items={nextFiveYears}
                                suffix={<ChevronDownIcon />}
                                label='Year'
                                labelSize='small'
                                name='endYear'
                                placeholder='Select'
                                validState={!!values.endYear}
                                value={values.endYear || ''}
                                onChange={handleYearChange}
                                error={getError('endYear')}
                                onBlur={onBlur}
                            />
                        </div>
                    </Controller>
                    {values.endMonth &&
                        values.endYear &&
                        errors.interestFreeEndDate && (
                            <div className='text-error-100 text-md -mt-5 inline'>
                                {getError('interestFreeEndDate')}
                            </div>
                        )}
                </>
            )}
            {values.interestFreePeriod !== undefined && (
                <>
                    <Controller label={interestLabel} labelSize='small'>
                        <TextField
                            className='w-2/3'
                            suffix='%'
                            maxLength={5}
                            mask='percentage'
                            name='interestRate'
                            value={values.interestRate?.toString() || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={getError('interestRate')}
                        />
                    </Controller>
                    <DebtConCallOut
                        classes='-mt-4'
                        text={
                            values.type === DebtLabelEnum.CreditCard
                                ? "It's usually around 23% for a credit card."
                                : undefined
                        }
                    />
                </>
            )}
        </>
    );
};

export default CreditCardMailOrderForm;
